import React, {useEffect, useMemo} from 'react';
import {createRefetchContainer, graphql} from 'react-relay';
import ProjectSprintsV3 from './project_sprints_v3';
import {withSocketHandling} from '../../../socket/withSocketHandling';
import {useForecastFetchQuery} from '../../shared/hooks/useForecastFetchQuery';
import {sprintsPredictionsQuery} from './ProjectSprintsPredictionsQuery';
import {keyBy} from 'lodash';
import {shouldShowTaskOverrun, trackPredictionsShown} from '../../shared/util/PredictionUtil';
import {PERMISSION_TYPE} from '../../../Permissions';

export const ProjectSprintWrapper = ({viewer, relay, retry, setSocketVariables, setSocketConfig, buyNowTime}) => {
	const {fetch, data: predictionData} = useForecastFetchQuery(sprintsPredictionsQuery);

	useEffect(() => {
		if (viewer.project && shouldShowTaskOverrun(viewer.project.estimationUnit === 'HOURS', PERMISSION_TYPE.SPRINT_UPDATE, viewer.availableFeatureFlags)) {
			fetch({projectId: '' + viewer.project.companyProjectId});
		} else if (viewer.projectGroup && shouldShowTaskOverrun(viewer.projectGroup.projects.edges[0].node.estimationUnit === 'HOURS', PERMISSION_TYPE.SPRINT_UPDATE, viewer.availableFeatureFlags)) {
			fetch({groupId: '' + viewer.projectGroup.companyProjectGroupId});
		}
	}, []);

	const predictionDataTasks = useMemo(() => {
		return (
			predictionData &&
			(predictionData.viewer.projectGroup
				? predictionData.viewer.projectGroup.projects.edges.reduce((acc, project) => acc.concat(project.node.tasks ? project.node.tasks.edges : []), []).map(predTask => predTask.node)
				: predictionData.viewer.project.tasks.edges.map(predTask => predTask.node))
		);
	}, [predictionData]);

	const predictionDataMap = useMemo(() => {
		return predictionDataTasks && keyBy(predictionDataTasks, o => o.id);
	}, [predictionDataTasks]);

	useEffect(() => {
		if (predictionDataTasks) {
			trackPredictionsShown(predictionDataTasks);
		}
	}, [predictionDataTasks]);

	const lowHighModelScore = predictionData && predictionData.viewer.company.availableMLModels.lowHighModelScore;

	const refetch = (variables, renderVariables, onSuccess) => {
		relay.refetch(variables, renderVariables, onSuccess);
		setSocketVariables(variables);
	};

	return <ProjectSprintsV3 viewer={viewer} predictionDataMap={predictionDataMap} lowHighModelScore={lowHighModelScore} relay={relay} retry={retry} setSocketConfig={setSocketConfig} refetch={refetch} buyNowTime={buyNowTime} />;
};

const ProjectSprintWrapperQuery = graphql`
	query ProjectSprintWrapper_Query($projectId: String, $groupId: String, $fetchLazyData: Boolean!, $onlyActiveSprint: Boolean!) {
		viewer {
			actualPersonId
			component(name: "project_sprints_optimized")
			project(id: $projectId) {
				id
			}
			projectGroup(id: $groupId) {
				projects(first: 1000000) {
					edges {
						node {
							id
						}
					}
				}
			}
			...ProjectSprintWrapper_viewer @arguments(projectId: $projectId, groupId: $groupId, fetchLazyData: $fetchLazyData, onlyActiveSprint: $onlyActiveSprint)
		}
	}
`;

export {ProjectSprintWrapperQuery};

export default withSocketHandling(
	createRefetchContainer(
		ProjectSprintWrapper,
		{
			viewer: graphql`
				fragment ProjectSprintWrapper_viewer on Viewer @argumentDefinitions(projectId: {type: "String"}, groupId: {type: "String"}, fetchLazyData: {type: "Boolean!"}, onlyActiveSprint: {type: "Boolean!"}) {
					id
					language
					email
					firstName
					lastName
					backendId
					harvestUser
					timerStartDate
					actualPersonId
					excludeFromCompanyLockedPeriod
					submitLockedDateYear
					submitLockedDateMonth
					submitLockedDateDay
					availableFeatureFlags {
						key
					}
					timerTask {
						id
					}
					projects(first: 1000000) {
						edges {
							node {
								id
								companyProjectId
								name
								status
								isInProjectGroup
								projectColor
							}
						}
					}
					filters(first: 1000000, projectId: $projectId, projectGroupId: $groupId) @connection(key: "Viewer_filters", filters: []) {
						edges {
							node {
								id
								name
								section
								value
								updatedAt
							}
						}
					}
					company {
						id
						backendId
						name
						currency
						harvestEnabled
						lockedPeriodYear
						lockedPeriodMonth
						lockedPeriodDay
						isUsingProjectAllocation
						modules {
							moduleType
						}
						roles(first: 1000000) {
							edges {
								node {
									id
									name
								}
							}
						}
						labels(first: 1000000) {
							edges {
								...LabelDropdown_labels
								node {
									id
									name
									color
								}
							}
						}
						clients(first: 1000000) {
							edges {
								node {
									id
									name
									logoId
									logoDefaultId
								}
							}
						}
						teams(first: 1000000) {
							edges {
								...TeamDropdown_teams
								node {
									id
									name
									teamPersons(first: 1000000) {
										edges {
											node {
												id
												person {
													id
												}
											}
										}
									}
								}
							}
						}
						monday
						tuesday
						wednesday
						thursday
						friday
						saturday
						sunday
					}
					projectGroup(id: $groupId) {
						id
						companyProjectGroupId
						name
						color
						projectGroupSprints(first: 1000000, lazyLoadData: true) @connection(key: "ProjectGroup_projectGroupSprints", filters: []) {
							edges {
								node {
									id
									name
									sprintPersons(first: 1000000) {
										edges {
											node {
												id
												availableMinutes @include(if: $fetchLazyData)
												scheduledMinutes @include(if: $fetchLazyData)
												taskAvailableMinutes @include(if: $fetchLazyData)
												person {
													id
													firstName
													lastName
													profilePictureId
													profilePictureDefaultId
													role {
														id
														name
													}
												}
											}
										}
									}
								}
							}
						}
						projects(first: 1000000) {
							edges {
								node {
									id
									name
									status
									companyProjectId
									taskLevels
									sprintTimeBox
									sprintLengthInDays
									useManualAllocations
									remainingAutoCalculated
									projectColor
									estimationUnit
									minutesPerEstimationPoint
									budgetWork
									budgetType
									rateCard {
										id
										currency
									}
									client {
										id
										name
									}
									projectPersons(first: 10000) {
										edges {
											node {
												person {
													id
													active
													permissions
													firstName
													lastName
													fullName
													initials
													profilePictureId
													profilePictureDefaultId
													role {
														id
														name
													}
													client {
														id
													}
												}
												role {
													id
													name
												}
											}
										}
									}
									statusColumnsV2(first: 1000000) {
										edges {
											node {
												id
												order
												name
												projectGroupStatusColumnId
												jiraStatusId
												category
											}
										}
									}
									phases(first: 1000) {
										edges {
											node {
												id
												name
											}
										}
									}
									sprints(first: 1000, onlyProjectGroupSprints: true) @connection(key: "Project_sprints", filters: []) {
										edges {
											node {
												id
												name
												projectGroupSprintId
												isProjectGroupSprint
												startDay
												startMonth
												startYear
												endDay
												endMonth
												endYear
												description @include(if: $fetchLazyData)
												savedPerformance
												hasUnfinishedTask
												integrations {
													id
													jiraId
													adoId
												}
											}
										}
									}
									tasks(first: 100000, approvedOnly: true, simpleResponse: true, activeSprint: $onlyActiveSprint) @connection(key: "Project_tasks", filters: []) {
										edges {
											node {
												id
												userCanDeleteTask
												userCantDeleteTaskReason
												name
												description
												highPriority
												companyTaskId
												estimateForecast
												estimateLow
												estimateHigh
												timeLeft @include(if: $fetchLazyData)
												approved
												sortOrder
												blocked
												bug
												estimateForecastPrice
												currentPrice @include(if: $fetchLazyData)
												deadlineYear
												deadlineMonth
												deadlineDay
												startYear
												startMonth
												startDay
												startFrom
												deadlineFrom
												hasDependency
												canStart
												canBeSetToDone
												subTaskCount
												latestUiUpdateAt
												hasLockedTime
												hasInvoicedTime
												favoured
												billable
												parentTaskId
												project {
													id
													estimationUnit
													minutesPerEstimationPoint
													companyProjectId
													projectEndYear
													projectEndMonth
													projectEndDay
													projectColor
													remainingAutoCalculated
													name
													status
													projectGroupId
													harvestProject {
														id
													}
													client {
														id
													}
													rateCard {
														id
														currency
													}
													useTaskHierarchy
												}
												taskLabels {
													id
													label {
														id
														name
														color
													}
												}
												sprint {
													id
													endYear
													endMonth
													endDay
													savedPerformance
													name
													projectGroupSprintId
													isProjectGroupSprint
												}
												statusColumnV2 {
													id
													name
													category
													projectGroupStatusColumnId
												}
												phase {
													id
													name
													deadlineYear
													deadlineMonth
													deadlineDay
												}
												role {
													id
													name
												}
												assignedPersons {
													id
													active
													firstName
													lastName
													profilePictureId
													profilePictureDefaultId
													permissions
													client {
														id
													}
												}
												taskDismissedNotifications {
													taskOverrunPredictionDismissed
												}
												timeRegistrations(first: 10000) @connection(key: "Task_timeRegistrations", filters: []) @include(if: $fetchLazyData) {
													edges {
														node {
															id
															minutesRegistered
															price
															invoiced
															person {
																id
																role {
																	id
																}
															}
														}
													}
												}
												thisTaskDependsOn(first: 1000) @connection(key: "Task_thisTaskDependsOn") {
													edges {
														node {
															id
															type
															thisDependsOnTask {
																id
																name
																deadlineYear
																deadlineMonth
																deadlineDay
																startYear
																startMonth
																startDay
															}
														}
													}
												}
												owner {
													id
													permissions
												}
												followers {
													id
													permissions
												}
												parentTask {
													id
													name
												}
												progress @include(if: $fetchLazyData)
											}
										}
									}
								}
							}
						}
					}
					projectGroups(first: 1000000) @connection(key: "Viewer_projectGroups", filters: []) {
						edges {
							node {
								id
								companyProjectGroupId
								name
							}
						}
					}
					project(id: $projectId) {
						id
						name
						isInProjectGroup
						projectGroupId
						status
						companyProjectId
						taskLevels
						sprintTimeBox
						sprintLengthInDays
						projectColor
						estimationUnit
						remainingAutoCalculated
						minutesPerEstimationPoint
						useManualAllocations
						useTaskFollowers
						useTaskOwner
						isJiraProject
						budgetWork
						budgetType
						rateCard {
							id
							currency
						}
						projectPersons(first: 1000000) {
							edges {
								node {
									person {
										id
										active
										permissions
										firstName
										lastName
										fullName
										initials
										profilePictureId
										profilePictureDefaultId
										role {
											id
											name
										}
										client {
											id
										}
									}
									role {
										id
										name
									}
								}
							}
						}
						statusColumnsV2(first: 1000000) {
							edges {
								node {
									id
									order
									name
									jiraStatusId
									category
								}
							}
						}
						phases(first: 1000000) {
							edges {
								node {
									id
									name
								}
							}
						}
						sprints(first: 1000000) @connection(key: "Project_sprints", filters: []) {
							edges {
								node {
									id
									name
									timeLeft
									forecast
									startDay
									startMonth
									startYear
									endDay
									endMonth
									endYear
									description @include(if: $fetchLazyData)
									savedPerformance
									hasUnfinishedTask
									sprintPersons(first: 1000000) {
										edges {
											node {
												id
												availableMinutes @include(if: $fetchLazyData)
												scheduledMinutes @include(if: $fetchLazyData)
												taskAvailableMinutes @include(if: $fetchLazyData)
												person {
													id
													firstName
													lastName
													profilePictureId
													profilePictureDefaultId
													role {
														id
														name
													}
												}
											}
										}
									}
									integrations {
										id
										jiraId
										adoId
									}
								}
							}
						}
						tasks(first: 100000, approvedOnly: true, simpleResponse: true, activeSprint: $onlyActiveSprint) @connection(key: "Project_tasks", filters: []) {
							edges {
								node {
									id
									userCanDeleteTask
									userCantDeleteTaskReason
									name
									description
									highPriority
									companyTaskId
									estimateForecast
									estimateLow
									estimateHigh
									timeLeft @include(if: $fetchLazyData)
									approved
									sortOrder
									currentPrice @include(if: $fetchLazyData)
									estimateForecastPrice
									blocked
									bug
									deadlineYear
									deadlineMonth
									deadlineDay
									startYear
									startMonth
									startDay
									startFrom
									deadlineFrom
									hasDependency
									canStart
									canBeSetToDone
									subTaskCount
									latestUiUpdateAt
									hasLockedTime
									hasInvoicedTime
									favoured
									billable
									parentTaskId
									taskLabels {
										id
										label {
											id
											name
											color
										}
									}
									sprint {
										id
										endYear
										endMonth
										endDay
										name
									}
									statusColumnV2 {
										id
										name
										category
									}
									phase {
										id
										name
										deadlineYear
										deadlineMonth
										deadlineDay
									}
									role {
										id
										name
									}
									assignedPersons {
										id
										active
										firstName
										lastName
										email
										profilePictureId
										profilePictureDefaultId
										permissions
										client {
											id
										}
									}
									taskDismissedNotifications {
										taskOverrunPredictionDismissed
									}
									timeRegistrations(first: 10000) @connection(key: "Task_timeRegistrations", filters: []) @include(if: $fetchLazyData) {
										edges {
											node {
												id
												minutesRegistered
												price
												invoiced
												person {
													id
													role {
														id
													}
												}
											}
										}
									}
									project {
										id
										name
										client {
											id
											name
										}
										harvestProject {
											id
										}
										remainingAutoCalculated
										companyProjectId
										estimationUnit
										minutesPerEstimationPoint
										projectEndYear
										projectEndMonth
										projectEndDay
										projectColor
										status
										useTaskHierarchy
									}
									thisTaskDependsOn(first: 1000) @connection(key: "Task_thisTaskDependsOn") {
										edges {
											node {
												id
												type
												thisDependsOnTask {
													id
													name
													deadlineYear
													deadlineMonth
													deadlineDay
													startYear
													startMonth
													startDay
												}
											}
										}
									}
									owner {
										id
										permissions
									}
									followers {
										id
										permissions
									}
									parentTask {
										id
										name
									}
									progress @include(if: $fetchLazyData)
								}
							}
						}
					}
				}
			`,
		},
		graphql`
			query ProjectSprintWrapperRefetchQuery($projectId: String, $groupId: String, $fetchLazyData: Boolean!, $onlyActiveSprint: Boolean!) {
				viewer {
					component(name: "project_sprints_optimized_refetch")
					...ProjectSprintWrapper_viewer @arguments(projectId: $projectId, groupId: $groupId, fetchLazyData: $fetchLazyData, onlyActiveSprint: $onlyActiveSprint)
				}
			}
		`
	)
);
